import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Area, AreaChart } from "recharts";
import { useRealtimeNotifications } from "@/hooks/dashboard/useRealtimeNotifications";
import { TrendingUp } from "lucide-react";

interface NotificationVolumeChartProps {
  timeRange: string;
  refreshInterval: number;
}

const NotificationVolumeChart: React.FC<NotificationVolumeChartProps> = ({
  timeRange,
  refreshInterval
}) => {
  const { volumeData, isLoading } = useRealtimeNotifications(timeRange, refreshInterval);

  const chartConfig = {
    count: {
      label: "Totaal",
      color: "hsl(var(--primary))",
    },
    resolved: {
      label: "Opgelost",
      color: "hsl(var(--success))",
    },
    pending: {
      label: "Openstaand",
      color: "hsl(var(--warning))",
    },
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Melding Volume</CardTitle>
            <CardDescription>Real-time volume trends</CardDescription>
          </div>
          <TrendingUp className="h-4 w-4 text-muted-foreground animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Melding Volume</CardTitle>
          <CardDescription>Real-time volume trends</CardDescription>
        </div>
        <TrendingUp className="h-4 w-4 text-green-500 animate-pulse" />
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={volumeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Area
                type="monotone"
                dataKey="pending"
                stackId="1"
                stroke={chartConfig.pending.color}
                fill={chartConfig.pending.color}
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="resolved"
                stackId="1"
                stroke={chartConfig.resolved.color}
                fill={chartConfig.resolved.color}
                fillOpacity={0.6}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke={chartConfig.count.color}
                strokeWidth={2}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export default NotificationVolumeChart;